import { useState, useSyncExternalStore, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { DialogFooter } from '@/components/ui/dialog.js';
import { acknowledgeGraphSearchNotice } from '@/lib/api/me.js';
import { logger } from '@/logger.js';
import { getState, subscribe } from '@/store/identity-store.js';

import { useHintState } from './hint-store.js';

/**
 * The user who closed the notice in this tab, so a remount or a refreshed identity that still says
 * required does not bring it back for them. Another account signed in later in the same tab still
 * gets its own notice. A reload asks identity again.
 */
let acknowledgedUserId: string | null = null;

/** Test-only: forget this tab's acknowledgment. */
export function _resetGraphSearchNoticeForTests(): void {
  acknowledgedUserId = null;
}

/**
 * One-time notice that corporate search now runs with the user's Microsoft sign-in. Identity decides
 * when it is due (`graphSearchNoticeRequired`); a stale cached identity never shows it. It waits for
 * the onboarding intro to close. Every way of closing counts as seen: it is a notice, not a choice.
 * A failed acknowledgment is logged and dropped, and identity asks again on the next load.
 */
export function GraphSearchNotice(): JSX.Element | null {
  const { identity, stale } = useSyncExternalStore(subscribe, getState);
  const { introSeen, introDismissed } = useHintState();
  const [closedForUserId, setClosedForUserId] = useState(acknowledgedUserId);

  const required = identity?.graphSearchNoticeRequired === true && !stale;
  if (
    !identity ||
    closedForUserId === identity.userId ||
    !required ||
    !(introSeen || introDismissed)
  ) {
    return null;
  }
  const userId = identity.userId;

  /** Closes the notice at once for this user and tells identity in the background. */
  function acknowledge(): void {
    acknowledgedUserId = userId;
    setClosedForUserId(userId);
    acknowledgeGraphSearchNotice().catch((error: unknown) => {
      logger.warn('Could not acknowledge the Graph search notice', error);
    });
  }

  return (
    <AppDialog
      open
      onOpenChange={(open) => !open && acknowledge()}
      title="Better search across your documents"
      description="To find answers in Corporate knowledge, CT Agent now searches on your behalf with your Microsoft sign-in. It only sees files you already have access to."
      className="sm:max-w-md"
    >
      <DialogFooter>
        <Button type="button" onClick={acknowledge}>
          Got it
        </Button>
      </DialogFooter>
    </AppDialog>
  );
}
